import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plugins } from '@leverege/plugin';
import queryString from 'query-string';
import I18N from '@leverege/i18n';
import useObjectKeysMemoize from './useObjectKeysMemoize.js';
import { EMPTY_OBJ, TwoFactoryVerifyDefaultProps } from '../Constants.js';
import useOnStateChange from './useOnChangeState.js';
import { defaultGetTwoFactorSettings, defaultRequest2FACode, getTwoFactorMethodsForUser } from '../Util.js';
const {
  t
} = I18N.ns('login.TwoFactorVerify');
export default function useTwoFactorVerify(opts) {
  const {
    auth,
    classNames = EMPTY_OBJ,
    variants: pVariants,
    options: pOptions,
    components: pComponents,
    onBackToLogin,
    onLogin,
    getTwoFactorSettings = defaultGetTwoFactorSettings,
    request2FACode = defaultRequest2FACode,
    onAfterLogin,
    onLoginError,
    ...restProps
  } = opts;
  const v = useObjectKeysMemoize(pVariants);
  const variants = useMemo(() => {
    return {
      ...TwoFactoryVerifyDefaultProps.variants,
      ...v
    };
  }, [v]);
  const o = useObjectKeysMemoize(pOptions);
  const options = useMemo(() => {
    return {
      ...TwoFactoryVerifyDefaultProps.options,
      ...o
    };
  }, [o]);
  const c = useObjectKeysMemoize(pComponents);
  const components = useMemo(() => {
    return {
      ...TwoFactoryVerifyDefaultProps.components,
      ...c
    };
  }, [c]);
  const rest = useObjectKeysMemoize(restProps);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [submitted, setSubmitted] = useState();
  const [twoFactorSettings, setTwoFactorSettings] = useState();
  const [selectedTwoFactorMethod, onChangeSelectedTwoFactorMethod] = useOnStateChange(getDefault2FAMethod(), true);
  const [requestedTwoFactorMessage, setRequestedTwoFactorMessage] = useState(selectedTwoFactorMethod?.submitInstruction);
  const userTwoFactorMethods = useRef(getTwoFactorMethodsForUser(auth.profile)
  // Plugins.get( 'TwoFactorAuthMethod' ).sort( ( a, b ) => {
  //   if ( a.sort > b.sort ) {
  //     return 1
  //   }

  //   return -1
  // } )
  );
  useEffect(() => {
    if (!auth.requires2FA) {
      onBackToLogin();
      return;
    }
    getTwoFactorSettings().then(settings => {
      setTwoFactorSettings(settings);
    }).catch(err => {
      setError(err);
    });
  }, []);
  const onSubmit = useCallback(({
    code,
    rememberMe
  }) => {
    setLoading(true);
    setError(null);
    const prom = onLogin?.(code.trim(), selectedTwoFactorMethod, rememberMe);
    if (prom?.then) {
      prom.then(res => {
        setSubmitted(true);
        return onAfterLogin?.(res);
      }).catch(err => {
        setError(err);
        onLoginError?.(err);
        setLoading(false);
      });
    } else {
      setSubmitted(true);
      setLoading(false);
    }
  }, [onLogin, onAfterLogin, onLoginError, selectedTwoFactorMethod]);
  const twoFactorMethod = Plugins.get('TwoFactorMethod', plg => {
    return plg.type === selectedTwoFactorMethod;
  })[0];
  const onRequest2FACode = useCallback(({
    data: method
  }) => {
    if (!method.isClientSide) {
      request2FACode(method.type).then(res => {
        setRequestedTwoFactorMessage(res.message);
      });
    } else {
      setRequestedTwoFactorMessage(method.submitInstruction || t('instruction'));
    }
  }, [request2FACode]);
  const onReset2FAMethod = useCallback(() => {
    setRequestedTwoFactorMessage(null);
    onChangeSelectedTwoFactorMethod({
      data: null
    });
  }, [onChangeSelectedTwoFactorMethod]);
  useEffect(() => {
    setRequestedTwoFactorMessage(auth.info);
  }, [auth.info]);
  return useMemo(() => {
    return {
      variants,
      classNames,
      components,
      options,
      auth,
      ...rest,
      onBackToLogin,
      state: {
        error,
        loading,
        onChangeSelectedTwoFactorMethod,
        onRequest2FACode,
        onReset2FAMethod,
        onSubmit,
        requestedTwoFactorMessage,
        selectedTwoFactorMethod,
        submitted,
        twoFactorMethod,
        twoFactorSettings,
        userTwoFactorMethods: userTwoFactorMethods.current
      }
    };
  }, [auth, classNames, components, error, loading, onBackToLogin, onChangeSelectedTwoFactorMethod, onRequest2FACode, onReset2FAMethod, onSubmit, options, requestedTwoFactorMessage, rest, selectedTwoFactorMethod, submitted, twoFactorMethod, twoFactorSettings, userTwoFactorMethods, variants]);
}
function getDefault2FAMethod() {
  const m = queryString.parse(window.location.search).m;
  const plgs = Plugins.get('TwoFactorAuthMethod', method => {
    return method.type === m;
  });
  return plgs?.[0];
}